import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useTheme,
  useMediaQuery,
  Avatar,
  Menu,
  MenuItem,
  ListItemAvatar,
  Divider,
  Badge,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Folder,
  People,
  Description,
  Assignment,
  Event,
  AttachMoney,
  Assessment,
  Security,
  Settings,
  Help,
  MenuBook,
  ChevronLeft,
  AccountCircle,
  Logout,
  Notifications,
  Business,
  Timer,
  ArrowBack,
  ViewKanban,
  SupervisorAccount,
  Insights,
  CreditCard,
  Web,
  ShowChart,
  DarkMode,
  LightMode,
  FolderShared,
  Email,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { useTenant } from '../../contexts/TenantContext';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';
import TimerWidget from '../Timer/TimerWidget';
import { NavAccess, ROUTE_ACCESS, canAccessNav } from '../../utils/permissions';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { unreadCount } = useNotifications();
  const { tenant, getBranding } = useTenant();
  const { mode, toggleTheme } = useAppTheme();
  const branding = getBranding();

  const drawerWidth = 240;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  type MenuItem = {
    text: string;
    icon: React.ReactElement;
    path: string;
    group?: string;
    access?: NavAccess;
  };

  const allMenuItems: MenuItem[] = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard', access: { kind: 'staff' } },
    { text: 'Cases', icon: <Folder />, path: '/cases', access: { kind: 'permission', permission: 'cases:read' } },
    { text: 'Clients', icon: <People />, path: '/clients', access: { kind: 'permission', permission: 'clients:read' } },
    {
      text: 'Portal Inbox',
      icon: <People />,
      path: '/portal-inbox',
      access: { kind: 'permission', permission: 'clients:read' },
      group: 'Communications',
    },
    {
      text: 'Mail',
      icon: <Email />,
      path: '/mail',
      access: { kind: 'permission', permission: 'emails:read' },
      group: 'Communications',
    },
    { text: 'Intake Pipeline', icon: <ViewKanban />, path: '/intake-pipeline', access: { kind: 'permission', permission: 'clients:read' } },
    { text: 'Firm Templates', icon: <Description />, path: '/templates', group: 'Documents', access: { kind: 'permission', permission: 'documents:read' } },
    { text: 'Court Forms', icon: <Description />, path: '/court-forms', group: 'Documents', access: { kind: 'permission', permission: 'documents:read' } },
    { text: 'Matter Documents', icon: <FolderShared />, path: '/documents', group: 'Documents', access: { kind: 'permission', permission: 'documents:read' } },
    {
      text: 'Matter Drive',
      icon: <Folder />,
      path: '/drive',
      group: 'Documents',
      access: { kind: 'permission', permission: 'documents:read' },
    },
    { text: 'Tasks', icon: <Assignment />, path: '/tasks', access: { kind: 'permission', permission: 'tasks:read' } },
    { text: 'Calendar', icon: <Event />, path: '/calendar', access: { kind: 'permission', permission: 'calendar:read' } },
    { text: 'Time Entries', icon: <Timer />, path: '/time-entries', access: { kind: 'permission', permission: 'time_entries:read' } },
    {
      text: 'Billing',
      icon: <AttachMoney />,
      path: '/billing',
      access: { kind: 'permission', permission: 'billing:read' },
    },
    {
      text: 'Invoices',
      icon: <AttachMoney />,
      path: '/invoices',
      access: { kind: 'permission', permission: 'billing:read' },
    },
    {
      text: 'Expense Approvals',
      icon: <AttachMoney />,
      path: '/expense-approvals',
      access: { kind: 'permission', permission: 'billing:write' },
    },
    {
      text: 'Subscription',
      icon: <CreditCard />,
      path: '/subscription',
      access: { kind: 'admin' },
    },
    {
      text: 'Report Center',
      icon: <Assessment />,
      path: '/report-center',
      access: { kind: 'permission', permission: 'reports:read' },
    },
    {
      text: 'Report Charts',
      icon: <Assessment />,
      path: '/reports',
      access: { kind: 'permission', permission: 'reports:read' },
    },
    {
      text: 'Staff Workload',
      icon: <Assessment />,
      path: '/staff-workload',
      access: { kind: 'admin' },
    },
    {
      text: 'Analytics',
      icon: <Insights />,
      path: '/analytics',
      access: { kind: 'admin' },
    },
    {
      text: 'Growth',
      icon: <ShowChart />,
      path: '/growth',
      access: { kind: 'platformAdmin' },
    },
    {
      text: 'Website CMS',
      icon: <Web />,
      path: '/cms',
      access: { kind: 'admin' },
    },
    {
      text: 'Team Management',
      icon: <SupervisorAccount />,
      path: '/users',
      access: { kind: 'admin' },
    },
    {
      text: 'Security',
      icon: <Security />,
      path: '/security',
      access: { kind: 'admin' },
    },
    {
      text: 'Settings',
      icon: <Settings />,
      path: '/settings',
      access: { kind: 'admin' },
    },
    {
      text: 'Tenant',
      icon: <Business />,
      path: '/tenant',
      access: { kind: 'admin' },
    },
  ];

  const menuItems = allMenuItems.filter((item) =>
    canAccessNav(user, item.access || ROUTE_ACCESS[item.path])
  );
  const canOpenFirmSettings = canAccessNav(user, { kind: 'admin' });

  const getCurrentPageTitle = () => {
    const currentItem = menuItems.find(item => item.path === location.pathname);
    return currentItem ? currentItem.text : 'Dashboard';
  };

  const isInCaseFolder = location.pathname.includes('/cases/') && location.pathname.includes('/folder');
  const getPreviousPath = () => {
    if (isInCaseFolder) {
      return '/cases';
    }
    return null;
  };

  const drawer = (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          {branding.logo ? (
            <Box
              component="img"
              src={branding.logo}
              alt={branding.companyName || tenant?.name || 'Logo'}
              sx={{ height: 28, maxWidth: 120, objectFit: 'contain' }}
            />
          ) : null}
          {branding.companyName || tenant?.name || 'Legal Dashboard'}
        </Typography>
        {isMobile && (
          <IconButton onClick={handleDrawerToggle}>
            <ChevronLeft />
          </IconButton>
        )}
      </Box>
      
      <List sx={{ pt: 1 }}>
        {menuItems.map((item, index) => {
          const prev = menuItems[index - 1] as { group?: string } | undefined;
          const showGroupLabel = item.group && item.group !== prev?.group;
          return (
            <React.Fragment key={item.text}>
              {showGroupLabel && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ px: 2.5, pt: 1, pb: 0.5, display: 'block', fontWeight: 600 }}
                >
                  {item.group}
                </Typography>
              )}
              <ListItem
                button
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setMobileOpen(false);
                }}
                selected={location.pathname === item.path}
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  mb: 0.5,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'white',
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItem>
            </React.Fragment>
          );
        })}
      </List>
      
      <Divider sx={{ my: 2 }} />
      
      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Need Help?
        </Typography>
        <ListItem button onClick={() => navigate('/help')}>
          <ListItemIcon>
            <Help />
          </ListItemIcon>
          <ListItemText primary="Help & Support" />
        </ListItem>
        <ListItem button onClick={() => navigate('/help/guide')}>
          <ListItemIcon>
            <MenuBook />
          </ListItemIcon>
          <ListItemText primary="User's Guide" />
        </ListItem>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: 'background.paper',
          color: 'text.primary',
          boxShadow: 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            {getPreviousPath() && (
              <IconButton
                onClick={() => navigate(getPreviousPath()!)}
                sx={{ mr: 2 }}
                color="inherit"
              >
                <ArrowBack />
              </IconButton>
            )}
            <Typography variant="h6" component="div">
              {getCurrentPageTitle()}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              color="inherit"
              onClick={toggleTheme}
              title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {mode === 'dark' ? <LightMode /> : <DarkMode />}
            </IconButton>
            <IconButton 
              color="inherit"
              onClick={() => navigate('/notifications')}
              sx={{ position: 'relative' }}
            >
              <Badge badgeContent={unreadCount} color="error">
                <Notifications />
              </Badge>
            </IconButton>
            
            <IconButton
              onClick={handleProfileMenuOpen}
              sx={{ ml: 1 }}
            >
              <Avatar sx={{ width: 32, height: 32 }}>
                {user?.firstName?.charAt(0) || 'A'}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
        }}
      >
        {children}
      </Box>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => navigate('/profile')}>
          <ListItemIcon>
            <AccountCircle fontSize="small" />
          </ListItemIcon>
          Profile
        </MenuItem>
        {canOpenFirmSettings && (
          <MenuItem onClick={() => navigate('/settings')}>
            <ListItemIcon>
              <Settings fontSize="small" />
            </ListItemIcon>
            Settings
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      {/* Global floating stopwatch — available on every authenticated page */}
      <TimerWidget />

    </Box>
  );
};

export default Layout; 